import { formatDate, formatNumber } from '@/lib/utils';
import { DataSource } from '@/types/data-source';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useExtractionHistory } from '@/hooks/use-data-sources';
import { Loader2, CheckCircle2, XCircle, Clock, Play } from 'lucide-react';

interface ExtractionHistoryProps {
  open: boolean;
  onClose: () => void;
  dataSource: DataSource | null;
}

const statusConfig = {
  pending: {
    variant: 'secondary' as const,
    icon: Clock,
    label: 'Pending',
  },
  running: {
    variant: 'warning' as const,
    icon: Play,
    label: 'Running',
  },
  completed: {
    variant: 'success' as const,
    icon: CheckCircle2,
    label: 'Completed',
  },
  failed: {
    variant: 'destructive' as const,
    icon: XCircle,
    label: 'Failed',
  },
};

export default function ExtractionHistory({
  open,
  onClose,
  dataSource,
}: ExtractionHistoryProps) {
  const { data, isLoading } = useExtractionHistory(dataSource?.id || '');

  const jobs = data?.jobs || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Extraction History</DialogTitle>
          <DialogDescription>
            {dataSource?.name
              ? `Extraction jobs for ${dataSource.name}`
              : 'Extraction job history'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Play className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p>No extraction jobs yet.</p>
            <p className="text-sm">
              Start an extraction from the data sources table.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => {
                const status = statusConfig[job.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                const progressPercent =
                  job.totalRows > 0
                    ? Math.round((job.processedRows / job.totalRows) * 100)
                    : 0;

                return (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono text-xs">
                      {job.jobId.substring(0, 8)}...
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={status.variant}
                        className="flex items-center gap-1 w-fit"
                      >
                        <StatusIcon
                          className={`h-3 w-3 ${
                            status.label === 'Running' ? 'animate-spin' : ''
                          }`}
                        />
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                          <div className="text-sm">
                        <div className="font-medium">
                          {formatNumber(job.processedRows)} / {formatNumber(job.totalRows)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          extracted
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-12 text-right">
                            {progressPercent}%
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Batch {job.currentBatch} / {job.totalBatches}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {job.startedAt ? formatDate(job.startedAt) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {job.completedAt ? formatDate(job.completedAt) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {jobs.length > 0 && (
          <div className="text-sm text-muted-foreground text-center pt-4 border-t">
            Showing {jobs.length} extraction job{jobs.length !== 1 ? 's' : ''}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
